<!-- UploadProgress.vue -->
<template>
  <div class="transcription-upload-progress" v-if="visible">
    <div class="progress-container">
      <button
        v-if="showCloseButton"
        class="close-button"
        @click="close"
        aria-label="Close"
      >
        ✕
      </button>

      <div class="progress-steps">
        <div
          v-for="(step, index) in steps"
          :key="step.id"
          class="progress-step"
          :class="{
            'active': currentStep === index,
            'complete': currentStep > index && !hasError,
            'pending': currentStep < index,
            'error': hasError && currentStep === index
          }"
        >
          <div class="step-icon">
            <span v-if="currentStep > index && !hasError">✓</span>
            <span v-else-if="hasError && currentStep === index">✗</span>
            <span v-else-if="currentStep === index" class="spinner">⟳</span>
            <span v-else>{{ index + 1 }}</span>
          </div>
          <div class="step-label">{{ step.label }}</div>
          <div v-if="step.sublabel" class="step-sublabel">{{ step.sublabel }}</div>
        </div>
      </div>

      <div class="progress-bar-container">
        <div
          ref="progressBar"
          class="progress-bar"
          :class="{ 'error-bar': hasError }"
        ></div>
      </div>

      <div class="progress-text" :class="{ 'error-text': hasError }">
        {{ currentStepLabel }} ({{ totalProgress }}%)
      </div>
    </div>
  </div>
</template>

<script>
import { applyElementStyles } from '../../utils/runtimeThemeSheet'

export default {
  data () {
    return {
      visible: false,
      currentStep: 0,
      uploadPercent: 0,
      hasError: false,
      steps: [
        { id: 'auth', label: 'Requesting token', sublabel: '' },
        { id: 'token', label: 'Token received', sublabel: '✓' },
        { id: 'upload', label: 'Uploading recording', sublabel: '' },
        { id: 'complete', label: 'Complete', sublabel: '✓' }
      ]
    }
  },

  computed: {
    currentStepLabel () {
      return this.steps[this.currentStep]?.label || ''
    },

    totalProgress () {
      // Step 0: 0-10%
      // Step 1: 10-20%
      // Step 2: 20-95% (based on upload)
      // Step 3: 95-100%

      if (this.currentStep === 0) return 5
      if (this.currentStep === 1) return 15
      if (this.currentStep === 2) return 20 + (this.uploadPercent * 0.75)
      if (this.currentStep === 3) return 100
      return 0
    },

    showCloseButton () {
      // Show close button when there's an error or when completed
      return this.hasError || this.currentStep === 3
    }
  },

  watch: {
    totalProgress () {
      this.syncProgressWidth()
    },
    visible () {
      this.$nextTick(() => this.syncProgressWidth())
    }
  },

  mounted () {
    this.syncProgressWidth()
  },

  methods: {
    syncProgressWidth () {
      applyElementStyles(this.$refs.progressBar, {
        width: `${this.totalProgress}%`
      })
    },
    start () {
      this.visible = true
      this.currentStep = 0
      this.uploadPercent = 0
      this.hasError = false
      // Reset all step sublabels
      this.steps.forEach((step, index) => {
        if (index === 1 || index === 3) {
          step.sublabel = '✓'
        } else {
          step.sublabel = ''
        }
      })
    },

    nextStep () {
      if (this.currentStep < this.steps.length - 1) {
        this.currentStep++
      }
    },

    setUploadProgress (percent) {
      this.uploadPercent = percent
    },

    complete () {
      this.currentStep = 3
      // Only auto-hide if there's no error
      if (!this.hasError) {
        setTimeout(() => {
          this.visible = false
        }, 2000)
      }
    },

    error (message) {
      this.hasError = true
      this.steps[this.currentStep].sublabel = `✗ ${message}`
      // Don't auto-hide on error - let user manually close
    },

    close () {
      this.visible = false
      // Reset state for next upload
      setTimeout(() => {
        this.currentStep = 0
        this.uploadPercent = 0
        this.hasError = false
      }, 300)
    }
  }
}
</script>

<style scoped>
.transcription-upload-progress {
  position: fixed;
  top: 20px;
  right: 20px;
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  min-width: 300px;
  z-index: 9999;
}

.progress-container {
  position: relative;
}

.close-button {
  position: absolute;
  top: -10px;
  right: -10px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f44336;
  color: white;
  border: none;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  z-index: 1;
}

.close-button:hover {
  background: #d32f2f;
}

.close-button:active {
  background: #b71c1c;
}

.progress-step {
  display: flex;
  align-items: center;
  margin: 10px 0;
  opacity: 0.4;
  transition: opacity 0.3s;
}

.progress-step.active,
.progress-step.complete {
  opacity: 1;
}

.progress-step.error {
  opacity: 1;
}

.step-icon {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  background: #e0e0e0;
  font-weight: bold;
  flex-shrink: 0;
}

.progress-step.active .step-icon {
  background: #2196F3;
  color: white;
}

.progress-step.complete .step-icon {
  background: #4CAF50;
  color: white;
}

.progress-step.error .step-icon {
  background: #f44336;
  color: white;
}

.step-label {
  font-weight: 500;
  color: #333;
}

.step-sublabel {
  margin-left: 8px;
  font-size: 0.85em;
  color: #666;
}

.progress-step.error .step-sublabel {
  color: #f44336;
  font-weight: 500;
}

.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.progress-bar-container {
  width: 100%;
  height: 6px;
  background: #e0e0e0;
  border-radius: 3px;
  margin: 15px 0;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  width: 0%;
  background: linear-gradient(90deg, #2196F3, #4CAF50);
  transition: width 0.3s ease;
}

.progress-bar.error-bar {
  background: #f44336;
}

.progress-text {
  font-size: 0.9em;
  color: #666;
  text-align: center;
}

.progress-text.error-text {
  color: #f44336;
  font-weight: 500;
}
</style>
